
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { ColumnHeader } from '@/interfaces/components/ColumnHeader';
import VTable from '@/components/shared/table/VTable.vue';
import PromoCodeFilter from '@/components/promo/PromoCodeFilter.vue';
import { Table } from '@/interfaces/models/Table';
import headers from './headers';
import { PromoCode } from '@/interfaces/models/PromoCode';
import { Venue } from '@/interfaces/models/Venue';
import { Permission } from '@/enums/Permission';
import { CustomerGroup as CustomerGroupEnum } from '@/enums/CustomerGroup';
import moment from 'moment';
import { TranslateResult } from 'vue-i18n';

@Component({
  components: { VTable, PromoCodeFilter },
})
export default class PromoCodeTable extends Vue {
  @Prop({ type: Array, required: true }) public customerGroups!: CustomerGroupEnum[];
  @Prop({ type: String, required: false }) public customerGroup!: string;
  @Prop({ type: Array, required: true }) public items!: Table[];
  @Prop({ type: Number, required: true }) public total!: number;
  @Prop({ type: Array, required: true }) public venues!: Venue[];
  @Prop({ type: Object, default: () => null }) public venue!: Venue;
  @Prop({ type: Array, default: () => [] }) public venueNames!: Array<Partial<Venue>>;

  public $refs!: {
    table: InstanceType<typeof VTable> & { selected: any[] };
  };

  public canEdit(item: PromoCode) {
    if (this.$isAdmin()) {
      return true;
    }
    if (!this.$can(Permission.PROMO_CODE_UPDATE)) {
      return false;
    }
    if (item.customerGroup && item.venues.length === 0 && !this.customerGroup) {
      return false;
    }
    if (item.venues && item.venues.length > 0) {
      for (const venue of item.venues) {
        if (!this.venues.find((v: Venue) => v._id === venue)) {
          return false;
        }
      }
    }

    return true;
  }

  get headers() {
    if (!this.$can(Permission.PROMO_CODE_VIEW_SIMPLE) && !this.$can(Permission.PROMO_CODE_VIEW_GLOBAL)) {
      return headers;
    }
    if (this.$can(Permission.ADMIN)) {
      return headers;
    }

    if (this.$can(Permission.PROMO_CODE_VIEW_GLOBAL)) {
      return headers.filter((h: ColumnHeader) => h.value !== 'customerGroup');
    }

    if (this.$can(Permission.PROMO_CODE_VIEW_SIMPLE)) {
      return headers.filter((h: ColumnHeader) => !['customerGroup', 'countries', 'states'].includes(h.value));
    }

    // should actually never happen
    return [];
  }

  get accessibleCustomerGroups() {
    if (this.$isAdmin()) {
      return this.customerGroups;
    }

    return this.customerGroup ? [this.customerGroup] : [];
  }

  public edit(item: PromoCode) {
    this.$router.push({ name: 'promo.edit', params: { id: item._id }, query: { ...this.$route.query } });
  }

  public getVenueNames(item: PromoCode) {
    return this.venueNames
      .filter((v: Partial<Venue>) => item.venues.includes(v._id as string))
      .map((v: Partial<Venue>) => v.name);
  }

  get codes() {
    // @ts-ignore
    return this.items.map((i: PromoCode) => {
      return { ...i, disabledBy: null };
    });
  }

  public canToggleActivation(val: boolean, item: PromoCode) {
    if (this.venues && this.venues.length > 0) {
      // if simple view, cannot deactivate global promo codes
      if (
        this.$can(Permission.PROMO_CODE_VIEW_SIMPLE) &&
        !this.$can(Permission.PROMO_CODE_VIEW_GLOBAL) &&
        !item.venues?.length
      ) {
        return false;
      }

      const venueIds: string[] = this.venues.map((v: Venue) => v._id);
      for (const venue of item.venues) {
        if (!venueIds.includes(venue)) {
          return false;
        }
      }
    }

    if (val) {
      return this.$can(Permission.PROMO_CODE_ACTIVATE);
    }
    return this.$can(Permission.PROMO_CODE_DEACTIVATE);
  }

  public canToggleVenueActivation(val: boolean) {
    if (val) {
      return this.$can(Permission.PROMO_CODE_ENABLE);
    }
    return this.$can(Permission.PROMO_CODE_DISABLE);
  }

  get codesWithDisabledStatus() {
    return this.codes.map((c: PromoCode) => {
      return {
        ...c,
        disabled: this.isDisabledByVenue(c),
      };
    });
  }

  public isDisabledByVenue(item: PromoCode): boolean {
    return this.venue && this.venue.disabledPromoCodes && this.venue.disabledPromoCodes.includes(item._id);
  }

  public getToDate(value: string): string | TranslateResult {
    if (!value) {
      return '';
    }

    return value !== '2099-12-31T00:00:00.000Z' ? moment(value).format('DD-MM-YYYY') : this.$t('promo.table.unlimited');
  }

  public getAmount(value: number): number | TranslateResult {
    if (!value) {
      return '';
    }

    return value !== 999999999 ? value : this.$t('promo.table.unlimited');
  }
}
